import React, { useState } from 'react';
import {
    DirigeantPMList,
    DirigeantPM,
    DirigeantPMWidgetCallbackArgs,
    useModal,
} from '@europrocurement/flexy-components';
import { deleteIcon, FaOptionIcon, informationIcon, preview } from '@europrocurement/l2d-icons';

import { Box } from '@mui/system';
import {
    Button,
    ClickAwayListener,
    Grid,
    IconButton,
    styled,
    Tooltip,
    tooltipClasses,
    TooltipProps,
    Typography,
    useTheme,
} from '@mui/material';
import { CustomOptionResolver, CustomOptionResolvers } from '@europrocurement/flexy-form';
import {
    FormulaireFormaliteApiObject,
    useFormaliteService,
    useFormulaireService,
} from '@europrocurement/l2d-domain';
import { useParcoursFormalite } from '@europrocurement/l2d-modules/modules/ParcoursFormalite/provider';
import { FormulaireFormaliteJsonld } from '@europrocurement/l2d-domain/openApi/ApiFormulaire';
import { castIriToId } from '@europrocurement/l2d-utils';
import { useSnackbar } from 'notistack';
import { jsonForm } from '@europrocurement/flexy-form/utils/types/formTypes';

import { faUserPlus } from '@fortawesome/pro-regular-svg-icons';
// eslint-disable-next-line import/no-cycle
import { customItemJsonProps } from '../customItemRenderer';
import { useListeSelectionDataResolver } from '../../../hooks/useListeSelectionDataResolver';
import DirigeantPMJson from './DirigeantPM.json';
import DirigeantPMModal from './DirigeantPMModal';

// Définition de la configuration pour chaque forme juridique
const legalFormConfig = {
    SARL: {
        rolesOptions: {
            '66': 'Personne ayant le pouvoir d’engager à titre habituel la société',
            '71': 'Commissaire aux comptes titulaire',
            '72': 'Commissaire aux comptes suppléant',
        },
        // Valeurs spécifiques pour les conditions d'affichage
        gerantRoleValue: 30,
        modalComponent: DirigeantPMModal,
    },
    SAS: {
        rolesOptions: {
            '35': 'Président',
            '38': 'Directeur général',
            '39': 'Directeur général délégué',
            '66': 'Personne ayant le pouvoir d’engager à titre habituel la société',
            '71': 'Commissaire aux comptes titulaire',
            '72': 'Commissaire aux comptes suppléant',
        },
        gerantRoleValue: 35,
        modalComponent: DirigeantPMModal,
    },
    SCI: {
        rolesOptions: {
            '30': 'Gérant',
            '29': 'Gérant et associé indéfiniment responsable',
            '71': 'Commissaire aux comptes titulaire',
            '72': 'Commissaire aux comptes suppléant',
            '75': 'Associé indéfiniment responsable',
            '130': 'Associé unique qui récupère le patrimoine',
        },
        gerantRoleValue: 30,
        modalComponent: DirigeantPMModal,
    },
    SCS: {
        rolesOptions: {
            '30': 'Gérant',
            '28': 'Gérant et associé indéfiniment et solidairement responsable',
            '64': 'Membre du conseil de surveillance',
            '66': 'Personne ayant le pouvoir d’engager à titre habituel la société',
            '71': 'Commissaire aux comptes titulaire',
            '72': 'Commissaire aux comptes suppléant',
            '131': 'Associé commandité',
            '132': 'Associé commanditaire',
            '74': 'Associé indéfiniment et solidairement responsable',
            '99': 'Autre',
        },
        gerantRoleValue: 30,
        modalComponent: DirigeantPMModal,
    },
    SNC: {
        rolesOptions: {
            '30': 'Gérant',
            '28': 'Gérant et associé indéfiniment et solidairement responsable',
            '71': 'Commissaire aux comptes titulaire',
            '72': 'Commissaire aux comptes suppléant',
            '74': 'Associé indéfiniment et solidairement responsable',
        },
        gerantRoleValue: 30,
        modalComponent: DirigeantPMModal,
    },
    SA: {
        rolesOptions: {
            '65': 'Administrateur',
            '135': 'Administrateur représentant les salariés',
            '130': 'Associé unique qui récupère le patrimoine',
            '99': 'Autre',
            '71': 'Commissaire aux comptes titulaire',
            '72': 'Commissaire aux comptes suppléant',
            '53': 'Directeur général',
            '70': 'Directeur général délégué',
            '64': 'Membre du conseil de surveillance',
            '51': "Président du conseil d'administration",
            '52': 'Président du directoire',
            '110': 'Vice-Président',
        },
        gerantRoleValue: 51,
        modalComponent: DirigeantPMModal,
    },
    GIE: {
        rolesOptions: {
            '65': 'Administrateur',
            '130': 'Associé unique qui récupère le patrimoine',
            '99': 'Autre',
            '71': 'Commissaire aux comptes titulaire',
            '72': 'Commissaire aux comptes suppléant',
            '14': 'Contrôleur des comptes',
            '11': 'Membre',
        },
        gerantRoleValue: 65,
        modalComponent: DirigeantPMModal,
    },
    SCA: {
        rolesOptions: {
            '131': 'Associé commandité',
            '132': 'Associé commanditaire',
            '74': 'Associé indéfiniment et solidairement responsable',
            '99': 'Autre',
            '71': 'Commissaire aux comptes titulaire',
            '72': 'Commissaire aux comptes suppléant',
            '53': 'Directeur général',
            '70': 'Directeur général délégué',
            '30': 'Gérant',
            '28': 'Gérant et associé indéfiniment et solidairement responsable',
            '64': 'Membre du conseil de surveillance',
            '51': "Président du conseil d'administration",
            '61': 'Président du conseil de surveillance',
        },
        gerantRoleValue: 30,
        modalComponent: DirigeantPMModal,
    },
    SCP: {
        rolesOptions: {
            '75': 'Associé indéfiniment responsable',
            '130': 'Associé unique qui récupère le patrimoine',
            '71': 'Commissaire aux comptes titulaire',
            '72': 'Commissaire aux comptes suppléant',
            '30': 'Gérant',
            '29': 'Gérant et associé indéfiniment responsable',
        },
        gerantRoleValue: 30,
        modalComponent: DirigeantPMModal,
    },
    LIQUIDATEUR: {
        rolesOptions: {
            '40': 'Liquidateur',
        },
        gerantRoleValue: 40,
        modalComponent: DirigeantPMModal,
    },
    SOCIETE_ETRANGERE: {
        rolesOptions: {
            '150': 'Avocat',
            '106': 'Comptable',
            '109': 'Mandataire fiscal',
            '77': 'Représentant fiscal d’une entreprise étrangère sans établissement en France',
        },
        gerantRoleValue: 77,
        modalComponent: DirigeantPMModal,
    },
    AGRICOLE: {
        rolesOptions: {
            '75': 'Associé indéfiniment responsable',
            '71': 'Commissaire aux comptes titulaire',
            '72': 'Commissaire aux comptes suppléant',
            '30': 'Gérant',
            '29': 'Gérant et associé indéfiniment responsable',
            '94': 'Membre non salarié participant aux travaux',
            '66': 'Personne ayant le pouvoir d’engager à titre habituel la société',
        },
        gerantRoleValue: 30,
        modalComponent: DirigeantPMModal,
    },
};

const DirigeantPMView: React.FunctionComponent<customItemJsonProps> = function ({
    formContext,
    props,
    legalForm, // Récupération de la prop legalForm
    visibility,
}) {
    formContext.watch(props.name);

    const { enqueueSnackbar } = useSnackbar();
    const { getCurrentPresta } = useParcoursFormalite();
    const presta = getCurrentPresta();
    const { formulairesApi } = useFormulaireService();
    const { formaliteApi } = useFormaliteService();
    const createListeSelectionDataResolver = useListeSelectionDataResolver();
    const [form, setForm] = useState<FormulaireFormaliteApiObject | null>(null);
    const { modalActions } = useModal();
    const theme = useTheme();

    React.useEffect(() => {
        if (!form && presta) {
            const fetchForm = async () => {
                const resFormality = await formaliteApi.apiFormalitesIdGet({
                    id: `${castIriToId(presta.formalite as unknown as string)}`,
                });
                if (resFormality.data.formulaire) {
                    const resFormulaire = await formulairesApi.apiFormulaireFormalitesIdGet({
                        id: `${castIriToId(resFormality.data.formulaire)}`,
                    });
                    if (resFormulaire.data) {
                        setForm(resFormulaire.data as FormulaireFormaliteJsonld);
                    }
                } else {
                    enqueueSnackbar("La formalité n'a pas de formulaire", { variant: 'error' });

                    throw new Error('no formulaire on formality');
                }
            };
            fetchForm();
        }
    }, [presta, enqueueSnackbar, form, formaliteApi, formulairesApi]);

    // Récupérer la configuration spécifique à la forme juridique, avec une valeur par défaut
    const currentConfig = legalForm
        ? legalFormConfig[legalForm as keyof typeof legalFormConfig]
        : legalFormConfig.SARL;

    // Cloner et adapter dynamiquement le JSON en fonction de la configuration
    const adaptedJson = JSON.parse(JSON.stringify(DirigeantPMJson));

    if (adaptedJson && currentConfig) {
        // Adaptation du champ 'role'
        const roleField = adaptedJson.pages[0].sections.find(
            (s: { name: string }) => s.name === 'roleEntreprise',
        );
        if (roleField) {
            roleField.valuesfrom.options = currentConfig.rolesOptions;
        }
    }

    const formjson = adaptedJson;

    const createFakeListResolvers = () => {
        const listeSelectionDataResolver: CustomOptionResolver = () => [
            { label: 'Option 1', value: 1 },
            { label: 'Option 2', value: 2 },
            { label: 'Option 3', value: 3 },
            { label: 'Option 4', value: 4 },
            { label: 'Option 5', value: 5 },
            { label: 'Option 6', value: 6 },
            { label: 'Option 7', value: 7 },
            { label: 'Option 8', value: 8 },
            { label: 'Option 9', value: 9 },
            { label: 'Option 10', value: 10 },
        ];
        return listeSelectionDataResolver;
    };

    const customOptionResolvers: CustomOptionResolvers = {
        listeSelectionDataResolver: form
            ? createListeSelectionDataResolver(form)
            : createFakeListResolvers(),
    };

    const onAdd = () => {
        modalActions.call(
            <DirigeantPMModal
                onChange={async (curr, pagelenth, data) => {
                    formContext.setValue(props.name, [
                        ...(formContext.getValues(props.name) || []),
                        {
                            dirigeantPM: {
                                ...data,
                            },
                        },
                    ]);
                    modalActions.close();
                }}
                json={formjson as jsonForm}
                defaultValues={{}}
                customOptionResolvers={customOptionResolvers}
                visibility={visibility}
            />,
            false,
            true,
        );
    };

    const onPreview: DirigeantPMWidgetCallbackArgs = (dirigeantPM, index) => {
        modalActions.call(
            <DirigeantPMModal
                onChange={async (curr, pagelenth, data) => {
                    const newValues = [...formContext.getValues(props.name)];
                    newValues.splice(index, 1, {
                        dirigeantPM: {
                            ...data,
                        },
                    });
                    formContext.setValue(props.name, newValues);
                    modalActions.close();
                }}
                json={formjson as jsonForm}
                defaultValues={{
                    ...dirigeantPM,
                }}
                customOptionResolvers={customOptionResolvers}
                visibility={visibility}
            />,
            false,
            true,
        );
    };

    const onDelete: DirigeantPMWidgetCallbackArgs = (dirigeantPM, index) => {
        const newValues = [...formContext.getValues(props.name)];

        newValues.splice(index, 1);

        formContext.setValue(props.name, newValues);
    };

    const LightTooltip = styled(({ className, ...propsTooltip }: TooltipProps) =>
        propsTooltip.onClose ? (
            <ClickAwayListener onClickAway={propsTooltip.onClose}>
                <Tooltip
                    {...propsTooltip}
                    classes={{ popper: className }}
                    PopperProps={{ sx: { '.MuiTooltip-tooltip': { bgcolor: 'green' } } }}
                />
            </ClickAwayListener>
        ) : (
            <Tooltip
                {...propsTooltip}
                classes={{ popper: className }}
                PopperProps={{ sx: { '.MuiTooltip-tooltip': { bgcolor: 'green' } } }}
            />
        ),
    )(() => ({
        [`& .${tooltipClasses.tooltip}`]: {
            backgroundColor: theme.palette.common.white,
            color: 'rgba(0, 0, 0, 0.87)',
            boxShadow: '1px 1px 15px 0px rgba(0, 0, 0, 0.2)',
            textAlign: 'justify',
            padding: '15px',
            marginBottom: '5px',
        },
    }));

    const [tooltipOpen, setTooltipOpen] = useState(false);

    const handleTooltipClose = () => {
        setTooltipOpen(false);
    };

    const handleTooltipToggle = () => {
        setTooltipOpen(!tooltipOpen);
    };

    return (
        <Box
            sx={{
                marginBottom: '5px',
                marginTop: '15px',
                display: 'block',
                flexDirection: 'row',
                justifyContent: 'space-between',
            }}
        >
            <Typography style={{ display: 'inline', paddingTop: '40px', fontWeight: '500' }}>
                Veuillez ajouter tout·e·s les dirigeant·e·s de la société :
            </Typography>
            {props.tooltip ? (
                <LightTooltip
                    key={props.tooltip.name}
                    title={
                        <Typography
                            variant="l2dbodyxs"
                            color="text.primary"
                            align="justify"
                        >
                            {props.tooltip.text}
                        </Typography>
                    }
                    open={tooltipOpen}
                    onClose={handleTooltipClose}
                    disableFocusListener
                    disableHoverListener
                    disableTouchListener
                    placement="top-end"
                >
                    <IconButton
                        color="secondary"
                        size="small"
                        sx={{
                            height: 'fit-content',
                            marginTop: '10px',
                        }}
                        onClick={handleTooltipToggle}
                    >
                        <FaOptionIcon {...informationIcon.props} />
                    </IconButton>
                </LightTooltip>
            ) : null}
            <DirigeantPMList
                items={(formContext.getValues(props.name) || []).map(
                    (dirig: { dirigeantPM: DirigeantPM }) => ({
                        ...dirig,
                        actions: [
                            {
                                name: 'preview',
                                icon: <FaOptionIcon {...preview.props} />,
                                onClick: onPreview,
                            },
                            {
                                name: 'delete',
                                icon: <FaOptionIcon {...deleteIcon.props} />,
                                onClick: onDelete,
                            },
                        ],
                    }),
                )}
            />
            <Grid
                container
                spacing={2}
            >
                <Grid
                    item
                    sm={12}
                    md={12}
                    lg={6}
                    xl={6}
                />
                <Grid
                    item
                    sm={12}
                    md={12}
                    lg={6}
                    xl={6}
                >
                    <Button
                        onClick={onAdd}
                        color="secondary"
                        style={{
                            width: '100%',
                            marginTop: '30px',
                            padding: '5px 15px',
                            fontSize: '1rem',
                            borderRadius: '15px',
                            right: '0',
                            border: '1px solid #aaa',
                            backgroundColor: theme.palette.background.paper,
                            color: theme.palette.secondary.main,
                        }}
                    >
                        <FaOptionIcon
                            size="sm"
                            data-testid="test-id-mimetype-icone"
                            icon={faUserPlus}
                            color={theme.palette.secondary.main}
                            sx={{ margin: '0 5px' }}
                        />
                        Ajouter un·e dirigeant·e·s
                    </Button>
                </Grid>
            </Grid>
        </Box>
    );
};

export default DirigeantPMView;
